import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";
import { FORM_CONTROL } from "./tableStyles";

export interface SearchableOption {
  value: string;
  label: string;
  /** Secondary text shown muted after the label (and matched by the search). */
  hint?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  /** Shown when nothing is selected. */
  placeholder: string;
  /** When set, adds a first option with value "" that clears the selection (e.g. "Not specified"). */
  clearLabel?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/**
 * Select box you can type into: opens a list, filters it as you type
 * (matches label and hint), arrow keys + Enter to pick, Escape to close.
 */
export default function SearchableSelect({ value, onChange, options, placeholder, clearLabel, disabled, id, className }: Props) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const selected = options.find((o) => o.value === value) ?? null;

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? options.filter((o) => `${o.label} ${o.hint ?? ""}`.toLowerCase().includes(q)) : options;
    return clearLabel && !q ? [{ value: "", label: clearLabel } as SearchableOption, ...filtered] : filtered;
  }, [options, query, clearLabel]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => { setActive(0); }, [query, open]);

  function close() {
    setOpen(false);
    setQuery("");
  }

  function pick(o: SearchableOption) {
    onChange(o.value);
    close();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      if (items[active]) pick(items[active]);
    } else if (e.key === "Escape" && open) {
      e.stopPropagation(); // don't also close a surrounding modal
      close();
    }
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && items[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        disabled={disabled}
        value={open ? query : selected ? (selected.hint ? `${selected.label} — ${selected.hint}` : selected.label) : ""}
        placeholder={open && selected ? (selected.hint ? `${selected.label} — ${selected.hint}` : selected.label) : placeholder}
        onFocus={() => !disabled && setOpen(true)}
        onClick={() => !disabled && setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        className={cn(FORM_CONTROL, "w-full pl-3 pr-9 py-2.5", disabled && "cursor-not-allowed")}
      />
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-surface border border-border rounded-xl shadow-lg py-1"
        >
          {items.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">No matches</li>}
          {items.map((o, i) => (
            <li
              key={o.value || "__clear"}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.value === value}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setActive(i)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer",
                i === active ? "bg-surface-muted" : "",
                o.value === "" ? "text-muted-foreground" : "text-foreground",
              )}
            >
              <span className="flex-1 min-w-0 truncate">
                {o.label}
                {o.hint && <span className="text-muted-foreground"> — {o.hint}</span>}
              </span>
              {o.value === value && o.value !== "" && <Check className="w-4 h-4 text-primary shrink-0" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
